import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DailyForecastCardV3 } from './DailyForecastCard.tsx';
import { SummaryForecastCardFromLink } from './SummaryForecastCard.tsx';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { useEditableMissionState } from '../missions/model/useEditableMissionState.tsx';
import { HourlyForecastCardFromLink } from './HourlyForecastCard.tsx';
import { Page } from '../components/page/Page.tsx';
import { formatLocalDate, formatLocalTimeRange, getTimezone } from '../support/helpers/format.ts';
import { PageHeading } from '../components/page/PageHeading.tsx';
import { WeatherAttribution } from './components/WeatherAttribution.tsx';
import { Refresher } from '../components/refresher/Refresher.tsx';
import { useState } from 'react';
import { WeatherMapPreviewCard } from './WeatherMapPreviewCard.tsx';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { dayjs } from '../support/helpers/dayjs.ts';

type ISO8601 = string;

export const MissionWeatherPage: React.FC<{missionId: string}> = ({ missionId }) => {
    usePageView('Mission Weather');

    const history = useInstance(HistoryViewModel);

    const mission = useEditableMissionState(missionId);

    const [refreshedAt, setRefreshedAt] = useState(Date.now());

    if (!mission) {
        return null;
    }

    const formattedMissionDate = formatLocalDate({ date: mission.getStartTime(), location: mission.getLocation() });
    const formattedMissionTimes = formatLocalTimeRange({ startTime: mission.getStartTime(), endTime: mission.getEndTime(), location: mission.getLocation() });

    const onRefresh = async () => {
        // show spinner for 1s
        await new Promise(res => setTimeout(res, 1000));

        // trigger refresh of panels below
        setRefreshedAt(Date.now());
    };

    const missionWeatherProps = {
        location: mission.getLocation(),
        startTime: mission.getStartTime(),
        endTime: mission.getEndTime()
    };

    const onClickDay = (day: ISO8601) => {
        mission.pages.hourlyWeather.go({
            startingAt: dayjs(day).tz(getTimezone(mission.getLocation())).hour(12).startOf('hour').toISOString(),
            from: 'DailyForecastCard'
        });
    };

    const onClickHour = (hour: ISO8601) => {
        mission.pages.hourlyWeather.go({ startingAt: hour, from: 'HourlyForecastCard' });
    };

    return (
        <Page title="Mission Weather" onBack={() => history.back(mission.pages.details.path())}>

            <Refresher onRefresh={onRefresh} />

            <PageHeading
                heading={`${mission.state.address.city}, ${mission.state.address.state}`}
                subheading={`${formattedMissionDate} | ${formattedMissionTimes}`}
            />

            <div key={refreshedAt}>
                <SummaryForecastCardFromLink link={mission.state.links.weatherHourly} {...missionWeatherProps} />
                <HourlyForecastCardFromLink link={mission.state.links.weatherHourly} {...missionWeatherProps} onHourClick={onClickHour} />
                <WeatherMapPreviewCard mission={mission} />
                <DailyForecastCardV3 link={mission.state.links.weatherDaily} {...missionWeatherProps} onForecastDayClick={onClickDay} />
            </div>

            <WeatherAttribution />

        </Page>
    );
};
